import React, { memo, useImperativeHandle, forwardRef } from 'react'
import PropTypes from 'prop-types'

import { DateAndTime } from '../Controls'
import { handleRequired, ErrorMessage, useFormBase, formImperativeHandleFunctions, handleFormPostObject } from './common'
import { FormFieldContainer } from './constants'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

/**
 * Implements a date and time control with associated dropdowns for forms.
 *
 * @param {object} props component props
 * @returns {DateAndTime} DateAndTime component
 */
const DateTime = memo(forwardRef((props, ref) => {
  const { t } = useTranslation()
  const { fixedErrorMessageFieldHeight, fieldTemplate, data, label, ...finalProps } = props

  const { onChange, errorMsgFixedHeight, disabled, setDisabled, fieldLabel, refFormElement, valueRef, errorMsg, setErrorMsg } = 
    useFormBase(props, fieldTemplate, data)

  useImperativeHandle(ref, () => ({
    ...formImperativeHandleFunctions(fieldTemplate, refFormElement, valueRef, setErrorMsg, setDisabled),
    validate: (postObject, refs) => {
      const value = valueRef.current
      let error = ''
      if (fieldTemplate.isRequired && !value) {
        error = handleRequired(props)
      } else if (fieldTemplate.validator) {
        const ret = fieldTemplate.validator(value)
        if (ret) {
          error = t(ret)
        } else {
          error = ''
        }
      } else if (fieldTemplate.maxLength && value.length > fieldTemplate.maxLength) {
        error = `${t('forms.maxFieldLength')} ${fieldTemplate.maxLength}.`
      } 

      return handleFormPostObject(postObject, fieldTemplate, value, refs, error, setErrorMsg)
    },
  }))

  const getDateValue = (dateObj) => {
    return moment(dateObj).format('YYYY-MM-DD')
  }

  return (
    <FormFieldContainer>
      <DateAndTime
        {...finalProps}
        disabled={finalProps.disabled !== undefined ? finalProps.disabled : disabled}
        ref={refFormElement}
        onChange={onChange}
        error={!!errorMsg}
        label={t(`${fieldLabel.current}`)}
        noTranslate={true}
        minDate={finalProps.minDate !== undefined ? typeof (finalProps.minDate) === 'object' ? getDateValue(finalProps.minDate) : finalProps.minDate : null}
        maxDate={finalProps.maxDate !== undefined ? typeof (finalProps.maxDate) === 'object' ? getDateValue(finalProps.maxDate) : finalProps.maxDate : null}
      />
      <ErrorMessage errorMsg={errorMsg} errorMsgFixedHeight={errorMsgFixedHeight} />
    </FormFieldContainer>
  )
}))

DateTime.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   Default value for the control.
   */
  defaultValue: PropTypes.string,
  /**
   value for the control.
   */
  value: PropTypes.string,
  /**
   Label to display for the control.
   */
  label: PropTypes.string,
  /**
   onChange function called when the value is changed.
   */
  onChange: PropTypes.func,
  /**
   Props applied to the label.
   */
  InputLabelProps: PropTypes.object,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Displays the control in an error state.
   */
  error: PropTypes.bool,
  /**
   Props applied to the Input element.
   */
  inputProps: PropTypes.object,
  /**
   Type for the control.
   */
  type: PropTypes.oneOf(['datetime-local', 'date', 'time', 'month', 'week']),
  /**
   Adds a fixed height at the bottom of the control for error messages.
   */
  fixedErrorMessageFieldHeight: PropTypes.bool,
  /**
   Field definition template for the control.
   */
  fieldTemplate: PropTypes.object.isRequired,
  /**
   The data object retrieved from the backend which contains all data for the form controls.
   */
  data: PropTypes.object,
  /**
   Set a ref to the component in your page and call this function to focus the cursor to this control.
   */
  'ref.current.focus': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to restore its default value contained in the fieldTemplate prop.
   */
  'ref.current.clear': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to get its current value.
   */
  'ref.current.getValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set its current value.
   */
  'ref.current.setValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to run the validators in the fieldTemplate prop.
   */
  'ref.current.validate': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to disable the control.
   */
  'ref.current.setDisabled': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set the error message for the control.
   */
  'ref.current.setErrorMsg': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to clear the error message for the control.
   */
  'ref.current.clearErrorMsg': PropTypes.func,
  /**
   Returns the form template associated with this form control.
   */
  'ref.current.getFormTemplate': PropTypes.func,
}

DateTime.defaultProps = {
  noTranslate: false,
  type: 'datetime-local'
}

export default DateTime
